/**
 * Pure helpers for the "Summit agent" pool used by the Listing Agent and Buyer
 * Agent dropdowns (SelectTeam with `onlySummitAgents`).
 *
 * Claritask 545619 + 546632: these dropdowns must populate from Team Members
 * whose Role is "Summit - Agent" (IS_AGENT === 'a') OR whose Title is
 * "Transaction Coordinator" (e.g. Bonnie, Anna S), sorted alphabetically by
 * last name.
 *
 * CommonJS (not ESM) so the same functions can be unit-tested with the
 * node-backend test harness; Babel's interop lets SelectTeam.js `import` them.
 */

var SUMMIT_AGENT_ROLE_CODE = 'a';
var TRANSACTION_COORDINATOR_TITLE = 'Transaction Coordinator';

function isSummitAgent(member) {
    return !!member && member.IS_AGENT === SUMMIT_AGENT_ROLE_CODE;
}

/**
 * Title equality, matching the Arc `exact` filter used in buildSummitAgentQuery
 * (case/whitespace tolerant). Kept as equality — not a substring test — so the
 * client-side pool and the server query agree on exactly the same people.
 */
function isTransactionCoordinator(member) {
    if (!member || member.TITLE == null) return false;
    return String(member.TITLE).trim().toLowerCase() === TRANSACTION_COORDINATOR_TITLE.toLowerCase();
}

/** A member belongs in the Listing/Buyer agent pool if they are a Summit Agent OR a Transaction Coordinator. */
function isInSummitAgentPool(member) {
    return isSummitAgent(member) || isTransactionCoordinator(member);
}

/**
 * Arc filter for the Summit-agent pool: Summit Agents OR Transaction
 * Coordinators. Returns { filter, filterType } to spread into the query.
 *
 * TITLE must use `exact`, NOT `contains`: this Arc backend silently ignores a
 * `contains` filter (alone it returns every row; inside an `or` the clause is
 * dropped entirely, which returned agents only and left Anna/Bonnie out).
 */
function buildSummitAgentQuery() {
    return {
        filter: [
            { code: 'IS_AGENT', text: SUMMIT_AGENT_ROLE_CODE, type: 'exact' },
            { code: 'TITLE', text: TRANSACTION_COORDINATOR_TITLE, type: 'exact' },
        ],
        filterType: 'or',
    };
}

/** Case-insensitive comparator by LAST_NAME, then FIRST_NAME as a tie-breaker. */
function compareByLastName(a, b) {
    var al = String((a && a.LAST_NAME) || '').toLowerCase().trim();
    var bl = String((b && b.LAST_NAME) || '').toLowerCase().trim();
    if (al !== bl) return al < bl ? -1 : 1;
    var af = String((a && a.FIRST_NAME) || '').toLowerCase().trim();
    var bf = String((b && b.FIRST_NAME) || '').toLowerCase().trim();
    if (af !== bf) return af < bf ? -1 : 1;
    return 0;
}

/** Case-insensitive comparator by FIRST_NAME, then LAST_NAME as a tie-breaker. */
function compareByFirstName(a, b) {
    var af = String((a && a.FIRST_NAME) || '').toLowerCase().trim();
    var bf = String((b && b.FIRST_NAME) || '').toLowerCase().trim();
    if (af !== bf) return af < bf ? -1 : 1;
    var al = String((a && a.LAST_NAME) || '').toLowerCase().trim();
    var bl = String((b && b.LAST_NAME) || '').toLowerCase().trim();
    if (al !== bl) return al < bl ? -1 : 1;
    return 0;
}

/** Return a new array of team members sorted alphabetically by last name. */
function sortTeamByLastName(list) {
    return (Array.isArray(list) ? list.slice() : []).sort(compareByLastName);
}

/** Listbox group an option belongs to. Terminated members sit in "inactive". */
function optionGroup(member) {
    return member && member.DELETED === 'y' ? 'inactive' : 'active';
}

/**
 * Merge the currently selected options with the fetched team list into the
 * option array the dropdown renders: unique by id, selected entries first so a
 * saved member survives even when they are not in the fetched page.
 *
 * Every option is stamped with `groupedBy`, which is what the dropdown groups
 * on. Selected options are carried over from the raw fetch and have no such
 * field, so without this a selected member formed a nameless group of its own
 * and split "ACTIVE" into two headers. (Claritask 546632)
 *
 * @param {Array} selectedOptions currently selected options (may be raw rows)
 * @param {Array} items           fetched team members, already ordered
 * @param {Function} getId        option → stable id (null when it has none)
 */
function mergeAgentOptions(selectedOptions, items, getId) {
    var map = new Map();

    function add(option) {
        var id = getId(option);
        if (!id || map.has(id)) return;
        map.set(id, option && option.groupedBy
            ? option
            : Object.assign({}, option, { groupedBy: optionGroup(option) }));
    }

    (Array.isArray(selectedOptions) ? selectedOptions : []).forEach(add);
    (Array.isArray(items) ? items : []).forEach(add);

    return Array.from(map.values());
}

module.exports = {
    SUMMIT_AGENT_ROLE_CODE: SUMMIT_AGENT_ROLE_CODE,
    TRANSACTION_COORDINATOR_TITLE: TRANSACTION_COORDINATOR_TITLE,
    isSummitAgent: isSummitAgent,
    isTransactionCoordinator: isTransactionCoordinator,
    isInSummitAgentPool: isInSummitAgentPool,
    buildSummitAgentQuery: buildSummitAgentQuery,
    compareByLastName: compareByLastName,
    compareByFirstName: compareByFirstName,
    sortTeamByLastName: sortTeamByLastName,
    optionGroup: optionGroup,
    mergeAgentOptions: mergeAgentOptions,
};
